import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import _ from "lodash";

import { Table } from "../../../../components/table/Table";
import { ReportTypes } from "../../../../helpers/reports.helper.types";
import { getBarchartReports } from "../../../../store/reports/reducers/barchart/barchart.selectors";
import {
  COLUMNS_ORDER,
  COLUMNS,
  initialSort,
  SEARCH_KEYS,
} from "./DrilldownTable.constants";
import { CsvDownload } from "../csvDownload/CsvDownload";
import { DrilldownRoutePage } from "../../drilldown.types";
import { Desktop, useIsDesktop } from "../../../../hocs/Responsive";
import { getSecureBlocksLimit } from "../../../../store/config.selectors";
import { DrilldownTableHeader } from "../drilldownTableHeader";
import { useIntlTranslate } from "../../../../hooks";

const GlobalFooter = ({ page }: { page: DrilldownRoutePage }) => (
  <div className={"drilldowns__table__footer-content"}>
    <div className={"bold"}>
      <FormattedMessage id={"additional_data_available_in_csv"} />
    </div>
    <CsvDownload page={page} />
  </div>
);

function getColumns(page) {
  const cols = COLUMNS[page];
  const order = COLUMNS_ORDER[page];

  return _.sortBy(cols, (col) => order.indexOf(col.accessor));
}

interface DrilldownSubHeader {
  limit: number;
}

const DrilldownSubHeader = ({ limit }: DrilldownSubHeader) => {
  return (
    <FormattedMessage
      id={"drilldown_table_row_limit_additional_data"}
      values={{ limit }}
    />
  );
};

interface DrilldownTableDesktop {
  page: DrilldownRoutePage;
  tableCount: number;
  data: any;
  isLoading?: boolean;
  dataFailure?: boolean;
  onRefreshClick?: () => void;
}

export const DrilldownTableDesktop = ({
  page,
  tableCount,
  data,
  isLoading,
  dataFailure,
  onRefreshClick,
}: DrilldownTableDesktop) => {
  const barchart = useSelector(getBarchartReports, _.isEqual);
  const limit = useSelector(getSecureBlocksLimit);
  const translate = useIntlTranslate();

  const isDesktop = useIsDesktop();
  const isNotBotnet = page !== DrilldownRoutePage.Botnet;
  const caption = translate(`aria_table_caption_drilldown_${page}`);
  const tableName = translate(`aria_table_name_drilldown_${page}`);

  const max = _.reduce(
    barchart,
    (sum, item) =>
      sum + (item[ReportTypes.Malware] || item[ReportTypes.Webfilter]),
    0
  );

  const columns = useMemo(() => getColumns(page), [page]);

  const renderGlobalFooter = ({ pageCount, state: { pageIndex } }) =>
    pageCount - 1 === pageIndex ? (
      <Desktop>
        <GlobalFooter page={page} />
      </Desktop>
    ) : null;

  return (
    <Table
      header={<DrilldownTableHeader page={page} />}
      columns={columns}
      data={data}
      defaultSorted={[initialSort]}
      renderGlobalFooter={isNotBotnet && tableCount < max && renderGlobalFooter}
      subHeader={
        isDesktop && isNotBotnet && <DrilldownSubHeader limit={limit} />
      }
      searchAriaLabelMessage="aria_search_for_drilldown_item"
      showPagination={!isLoading}
      searchKeys={SEARCH_KEYS}
      caption={caption}
      tableName={tableName}
      isLoading={isLoading}
      dataFailure={dataFailure}
      onRefreshClick={onRefreshClick}
    />
  );
};
